// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartLineBreakpoints } from "@hardwoods/Overrides/Widgets/Cart/helper";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { Typography } from "@insite/mobius/index";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { FC } from "react";
import { css } from "styled-components";

export interface XngHeadCartLineStyles {
    container?: InjectableCss;
    infoRightItem?: InjectableCss;
    infoContainer?: InjectableCss;
    infoRightContainer?: InjectableCss;
    hide?: InjectableCss;
    show?: InjectableCss;
    text?: TypographyPresentationProps;
}

interface OwnProps {
    rightChildren?: string[];
    leftHeaderTitle?: string;
    leftSize?: string;
    rightSize?: string;
    rightHideBreakpoint?: string;
    extendedStyles?: XngHeadCartLineStyles;
}

export const headCartLineStyles: XngHeadCartLineStyles = {
    container: {
        css: css`
            display: flex;
            height: 38px;
            align-items: center;
            padding-right: 0;
            background: ${color("common.ui3")};
            width: 100%;
        `,
    },
    infoRightItem: {
        css: css`
            display: flex;
            justify-content: flex-start;
            padding-right: 15px;
            @media print {
                justify-content: flex-start;
            }
        `,
    },
    text: {
        size: 14,
        css: css`
            font-weight: bold;
            letter-spacing: 0;
            line-height: 20px;

            @media print {
                word-break: keep-all;
            }
        `,
    },
    infoContainer: {
        css: css`
            flex-grow: 1;
            flex-shrink: 0;
            padding-left: 15px;
        `,
    },
    infoRightContainer: {
        css: css`
            flex-grow: 0;
            flex-shrink: 1;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;
            display: flex;
            flex-basis: ${({ rightSize }: any) => rightSize || "30%"};
            ${({ rightHideBreakpoint }: any) => css`
                ${xScreen.lte(`${rightHideBreakpoint || "lg"}`)} {
                    display: none;
                }
            `};

            @media print {
                display: none;
            }
        `,
    },
    show: {
        css: css`
            display: block;
        `,
    },
    hide: {
        css: css`
            display: none;
        `,
    },
};

const XngCartLinesHeader: FC<OwnProps> = ({
    rightChildren,
    leftHeaderTitle,
    leftSize,
    rightSize,
    rightHideBreakpoint,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(headCartLineStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.infoContainer} style={{ flexBasis: leftSize }}>
                <Typography {...styles.text}>{translate(leftHeaderTitle || "")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.infoRightContainer} {...{ rightHideBreakpoint, rightSize }}>
                {rightChildren?.map((item, index) => (
                    <StyledWrapper
                        key={uniqueId()}
                        {...styles.infoRightItem}
                        style={{ flexBasis: `${100 / rightChildren?.length}%` }}
                        className={`${!item ? "empty-item" : ""}`}
                        data-test-selector={`xngCartLinesHeader-infoRightItem-${item ? item?.toLowerCase() : index}`}
                    >
                        <Typography {...styles.text}>{translate(item || "")}</Typography>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

XngCartLinesHeader.defaultProps = {
    leftHeaderTitle: "Item Description",
    rightChildren: ["Price / Unit", "Qty", "Total"],
    leftSize: "58%",
    rightSize: "42%",
    rightHideBreakpoint: `${cartLineBreakpoints.md}`,
};

export default XngCartLinesHeader;
